<template>
    <div class="line-chart-container">
        <div class="chart-header" style="height: 6rem;">
            <div class="chart-title">
                {{ groupedCharts?.length ? [chartData, ...groupedCharts].map(c => c.title).join(' & ') : chartData.title }}
            </div>
            <div class="chart-subtitle">{{ chartData.timeGrouping?.toUpperCase?.() }}</div>
            <div class="time-filter-container">
                <TimeGranularityDateFilter
                    :timeGrouping="chartData.timeGrouping"
                    v-model="dateFilter"
                    @fetch="onFilterFetch"
                />
            </div>
        </div>
        <div class="chart-container">
            <v-progress-circular v-show="loading" class="loading-spinner" indeterminate color="#4BC0C0" size="56" />

            <div v-show="!loading" class="chart-wrapper">
                <canvas ref="chartCanvas"></canvas>
            </div>
        </div>
    </div>
</template>

<script>
import { Chart, registerables } from "chart.js";
import { getChartData } from "../services/chartService.js";
import TimeGranularityDateFilter from "./TimeGranularityDateFilter.vue";

Chart.register(...registerables);

const SINGLE_CHART_COLOR = { border: "rgba(75, 192, 192, 1)", bg: "rgba(75, 192, 192, 0.1)" };

function getLineColor(index) {
    const hue = (index * 137) % 360;
    return {
        border: `hsla(${hue}, 70%, 50%, 1)`,
        bg:     `hsla(${hue}, 70%, 50%, 0.1)`,
    };
}

export default {
    props: {
        chartData: {
            type: Object,
            required: true,
        },
        groupedCharts: {
            type: Array,
            default: null
        },
        projectId: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            loading: false,
            
            chartLabel: "",
            chartInstance: null,
            labels: [],
            datasets: [],
            isMounted: false,
            dateFilter: null,
        };
    },
    async created() {
        this.loading = true;
        this.metricKey = this.chartData.dimensions.BusinessMetric
            || this.chartData.dimensions.SystemMetric;
        this.chartLabel = this.chartData.customLabel || this.metricKey;
    },
    async mounted() {
        this.isMounted = true;
        await this.initializeChart();
    },
    beforeUnmount() {
        this.cleanupChart();
    },
    methods: {
        destroyChartInstance() {
            if (this.chartInstance) {
                this.chartInstance.destroy();
                this.chartInstance = null;
            }
        },
        cleanupChart() {
            this.isMounted = false;
            this.destroyChartInstance();
        },
        async initializeChart() {
            await this.loadChartData(null);
        },
        async onFilterFetch(filterPayload) {
            await this.loadChartData(filterPayload);
        },
        async loadChartData(filterPayload) {
            if (!this.isMounted) return;

            this.loading = true;
            try {
                const filter = filterPayload != null ? filterPayload : this.dateFilter;
                const allCharts = [this.chartData, ...(this.groupedCharts || [])];

                const results = await Promise.all(allCharts.map(chart => {
                    const metricKey = chart.dimensions.BusinessMetric || chart.dimensions.SystemMetric;
                    return getChartData(chart.id, this.projectId, metricKey, filter);
                }));

                if (!this.isMounted) return;

                this.labels = results[0].labels;
                this.datasets = results.map((res, i) => {
                    const chart = allCharts[i];
                    const metricKey = chart.dimensions.BusinessMetric || chart.dimensions.SystemMetric;
                    const color = this.groupedCharts?.length ? getLineColor(i) : SINGLE_CHART_COLOR;
                    return {
                        label: chart.customLabel || metricKey,
                        data: res.data,
                        borderColor: color.border,
                        backgroundColor: color.bg,
                    };
                });

                this.formatLabels();
                await this.$nextTick();
                this.renderChart();
            } catch (error) {
                console.error("Error loading groupKey chart matrices:", error);
            } finally {
                if (this.isMounted) this.loading = false;
            }
        },
        formatLabels() {
            this.labels = this.labels.map(label => {
                const date = new Date(label);
                if (this.chartData.timeGrouping === 'hourly') {
                    return new Intl.DateTimeFormat('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                        hourCycle: 'h23'
                    }).format(date);
                }
                if (this.chartData.timeGrouping === 'daily' || this.chartData.timeGrouping === 'weekly') {
                    return new Intl.DateTimeFormat('en-GB', {
                        day: '2-digit',
                        month: 'short'
                    }).format(date).replace(',', '');
                }
                if (this.chartData.timeGrouping === 'monthly') {
                    return new Intl.DateTimeFormat('en-GB', {
                        year: 'numeric',
                        month: 'short'
                    }).format(date).replace('-', ' ');
                }
                if (this.chartData.timeGrouping === 'yearly') {
                    return new Intl.DateTimeFormat('en-GB', {
                        year: 'numeric'
                    }).format(date);
                }
                return label;
            });
        },
        renderChart() {
            if (!this.isMounted || !this.$refs.chartCanvas) return;

            this.destroyChartInstance();

            const ctx = this.$refs.chartCanvas.getContext('2d');
            if (!ctx) return;

            this.chartInstance = new Chart(ctx, {
                type: "line",
                data: {
                    labels: this.labels,
                    // Iterates through dynamic internal datasets built in your loadChartData method
                    datasets: this.datasets.map(ds => ({
                        label: ds.label,
                        data: ds.data,
                        borderColor: ds.borderColor,
                        backgroundColor: ds.backgroundColor,
                        borderWidth: 2,
                        pointRadius: 5,
                        pointBackgroundColor: "#FFFFFF",
                        fill: true,
                        tension: 0.1 // Smooth out curve transitions slightly for accurate visualization data points
                    })),
                },
                options: {
                    animation: {
                        duration: 0
                    },
                    scales: {
                        x: {
                            ticks: {
                                maxTicksLimit: 10
                            }
                        }
                    },
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            display: true, // Forces display of split color labels at top
                            position: "top",
                        },
                        title: {
                            display: false,
                        },
                        subtitle: {
                            display: false,
                        }
                    },
                },
            });
        },
    },
    components: {
        TimeGranularityDateFilter,
    },
};
</script>

<style scoped>
.line-chart-container {
    width: 100%;
}

.chart-header {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 2px;
    margin-bottom: 4px;
}

.chart-title {
    text-align: center;
    font-weight: 600;
    font-size: 13px;
    color: #333;
}

.chart-subtitle {
    text-align: center;
    font-style: italic;
    font-weight: 600;
    font-size: 10px;
    color: #666;
}

.time-filter-container {
    width: 100%;
}

.chart-container {
    position: relative;
    width: 100%;
    height: 350px;
    display: flex;
    flex-direction: column;
}

.loading-spinner {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 2;
}

.chart-wrapper {
    position: relative;
    width: 100%;
    display: flex;
    flex: 1;
    min-height: 0;
    align-items: center;
    justify-content: center;
}

canvas {
    width: 100% !important;
    height: 100% !important;
}
</style>